import React, { useState } from 'react';
import createGridCustomCells from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { DateCell, YesNoCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import {
    SubjectMonitoringReviewTypeDto,
    SubjectMonitoringReviewTypePostDto,
} from '@interfaces-clean/subject-monitoring-review-type.interface';
import AddReviewTypeDialog, {
    AddReviewTypeForm,
} from '@pages-clean/organisation/subject-monitoring/manage-reviews/dialogs/add-review-type-dialog';
import EditReviewTypeDialog from '@pages-clean/organisation/subject-monitoring/manage-reviews/dialogs/edit-review-type-dialog';
import { AuditApplicationArea, AuditDataType, AuditServiceType } from 'types/graphql-global-types';
import { useAuthorization, useLocalState, useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromCurrentUtcDate } from '@utils/convertor';
import NotificationService from '@utils/notification';
import { SubjectMonitoringReviewTypeAPI } from '@apis/bff-api/organisation/subject-monitoring-review-type';

const ManageReviews = () => {
    const { t } = useTranslation('admin');
    const { TooltipCell } = createGridCustomCells();
    const { state } = useLocalState();
    const orgName = state?.selectedOrganisation?.name ?? '';
    const { data: reviewTypes, mutate } = SubjectMonitoringReviewTypeAPI.useGetAll();
    const { trigger: addReviewType, isMutating: isAdding } = SubjectMonitoringReviewTypeAPI.useAdd();

    const [selectedRecord, setSelectedRecord] = useState<SubjectMonitoringReviewTypeDto | null>(null);
    const { trigger: updateReviewType, isMutating: isUpdating } = SubjectMonitoringReviewTypeAPI.useUpdate(
        selectedRecord?.recordId ?? null,
    );

    const user = useAuthorization();
    const canEdit = !!user?.canWriteSystemAdminLevel?.();
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);

    const rows = Array.isArray(reviewTypes) ? reviewTypes : [];
    const existingNames = rows.map((r) => r.name ?? '');

    const handleAdd = async (data: AddReviewTypeForm) => {
        try {
            await addReviewType(data);
            await mutate();
            setShowAddDialog(false);
            NotificationService.showNotification({ text: t('common:actionsSuccessfulToast'), type: 'success' });
        } catch {
            NotificationService.showNotification({ text: t('common:errorMessage'), type: 'error' });
        }
    };

    const handleUpdate = async (data: SubjectMonitoringReviewTypePostDto) => {
        try {
            await updateReviewType(data);
            await mutate();
            setShowEditDialog(false);
            setSelectedRecord(null);
            NotificationService.showNotification({ text: t('common:actionsSuccessfulToast'), type: 'success' });
        } catch {
            NotificationService.showNotification({ text: t('common:errorMessage'), type: 'error' });
        }
    };

    const handleEditClose = () => {
        setShowEditDialog(false);
        setSelectedRecord(null);
    };

    const columns: GridColumnProps[] = [
        {
            field: 'recordId',
            title: t('smReviewTypes.grid.columns.recordId'),
            filter: 'numeric',
            width: 1,
            minWidth: 40,
            showByDefault: false,
        },
        {
            field: 'active',
            title: t('smReviewTypes.grid.columns.active'),
            filter: 'boolean',
            width: 1,
            minWidth: 40,
            cells: { data: YesNoCell },
        },
        {
            field: 'name',
            title: t('smReviewTypes.grid.columns.name'),
            filter: 'text',
            width: 1,
            minWidth: 140,
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            field: 'description',
            title: t('smReviewTypes.grid.columns.description'),
            filter: 'text',
            width: 1,
            minWidth: 240,
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            field: 'createdBy',
            title: t('smReviewTypes.grid.columns.createdBy'),
            filter: 'text',
            width: 1,
            minWidth: 140,
        },
        {
            field: 'createdOn',
            title: t('smReviewTypes.grid.columns.createdOn'),
            filter: 'date',
            width: 1,
            minWidth: 100,
            cells: { data: DateCell },
            cellName: 'DateCell',
        },
        {
            field: 'updatedBy',
            title: t('smReviewTypes.grid.columns.updatedBy'),
            filter: 'text',
            width: 1,
            minWidth: 140,
        },
        {
            field: 'updatedOn',
            title: t('smReviewTypes.grid.columns.updatedOn'),
            filter: 'date',
            width: 1,
            minWidth: 100,
            cells: { data: DateCell },
            cellName: 'DateCell',
        },
    ];

    return (
        <div className="k-h-full">
            {showAddDialog && (
                <AddReviewTypeDialog
                    existingNames={existingNames}
                    isSubmitting={isAdding}
                    onClose={() => setShowAddDialog(false)}
                    onSubmit={handleAdd}
                />
            )}
            {showEditDialog && selectedRecord && (
                <EditReviewTypeDialog
                    record={selectedRecord}
                    existingNames={existingNames}
                    isSubmitting={isUpdating}
                    onClose={handleEditClose}
                    onSubmit={handleUpdate}
                />
            )}
            <Grid
                title={t('smReviewTypes.grid.title')}
                testDataId="manage-reviews-grid"
                columns={columns}
                data={rows}
                maxHeight="auto"
                exportFilename={`${orgName}_Subject Monitoring Review Types_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-YYYY')}`}
                service={AuditServiceType.SUBJECT_MONITORING}
                area={AuditApplicationArea.SUBJECT_MONITORING}
                dataType={AuditDataType.EXCEL_EXPORT}
                sortable
                showAddIcon
                canEdit={canEdit}
                addCallback={() => setShowAddDialog(true)}
                dataItemKey="recordId"
                rowMenuActions={[
                    {
                        id: 1,
                        label: t('smReviewTypes.grid.editAction'),
                        onClick: (dataItem: unknown) => {
                            setSelectedRecord(dataItem as SubjectMonitoringReviewTypeDto);
                            setShowEditDialog(true);
                        },
                        icon: ['far', 'pen-to-square'],
                        disabled: !canEdit,
                    },
                ]}
            />
        </div>
    );
};

export default ManageReviews;
